import { useEffect, useState } from "react";

// Small shared helpers for the Electron desktop shell. `window.moovbyte`
// only exists when running inside Electron (exposed by desktop/preload.js),
// so every export here is a no-op/false in the plain browser app.
export const isDesktop = () => typeof window !== "undefined" && !!window.moovbyte;

// Style for an element that should let the user drag the (frameless,
// titleBarStyle:"hidden") window by its top chrome. Applied directly to an
// EXISTING, already-non-interactive element (e.g. the sidebar's logo strip)
// rather than a separate overlay -- an overlay div would sit in front of
// whatever's underneath and swallow clicks on real links/buttons there
// (Login's "back to home" logo link, for one), since -webkit-app-region
// only changes drag-gesture handling, not normal click propagation, and
// z-index stacking still governs which element actually receives the hit.
export const dragRegionStyle = isDesktop() ? { WebkitAppRegion: "drag" } : undefined;

// Applied to any interactive element that happens to sit inside a drag
// region (a logo that's also a Link, a button in a toolbar) so it stays
// clickable -- Chromium honors a nested no-drag region overriding its
// draggable ancestor.
export const noDragRegionStyle = isDesktop() ? { WebkitAppRegion: "no-drag" } : undefined;

// The window is created with titleBarStyle:"hidden" +
// titleBarOverlay:{...} (desktop/main.js) -- Windows keeps its native
// minimize/maximize/close buttons, but Chromium paints them (and their
// solid background box) directly ON TOP of the page in the top-right
// corner, completely outside normal CSS stacking. Any of our own UI
// (the header's user-menu button, in practice) that happens to render
// underneath that box gets visually covered/clipped by it -- padding
// alone can't fix this without knowing exactly how wide that box is.
//
// The Window Controls Overlay API (navigator.windowControlsOverlay) is
// how Chromium reports that box's live geometry -- it changes with DPI,
// Windows theme/accessibility text-size settings, and window
// maximize/restore, so this reads it dynamically rather than hardcoding
// a guessed pixel width. Returns 0 (no adjustment) in the plain browser,
// on any platform where the overlay isn't visible, or before the API is
// available (e.g. very first paint) -- callers should combine this with
// their own safe minimum padding so early layout doesn't collide either.
export function useWindowControlsOverlayInset() {
    const [inset, setInset] = useState(() => _readOverlayInset());

    useEffect(() => {
        const wco = typeof navigator !== "undefined" && navigator.windowControlsOverlay;
        if (!wco) return undefined;
        const update = () => setInset(_readOverlayInset());
        update();
        wco.addEventListener("geometrychange", update);
        return () => wco.removeEventListener("geometrychange", update);
    }, []);

    return inset;
}

function _readOverlayInset() {
    const wco = typeof navigator !== "undefined" && navigator.windowControlsOverlay;
    if (!wco || !wco.visible) return 0;
    try {
        // getTitlebarAreaRect() is the DRAGGABLE titlebar area, i.e. the
        // full top strip MINUS the native caption buttons on the right --
        // so the gap between its right edge and the window's own width is
        // exactly the reserved space those buttons occupy.
        const rect = wco.getTitlebarAreaRect();
        return Math.max(0, Math.round(window.innerWidth - rect.right));
    } catch {
        return 0;
    }
}
